import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { TourStep } from '../data/tourSteps';

export interface OnboardingTourProps {
  isOpen: boolean;
  steps: TourStep[];
  onDismiss: () => void;
}

// 대상 미렌더 대비 rAF 재시도 상한 — 초과 시 해당 스텝 자동 스킵(§R669-2 error 상태)
const MAX_TARGET_RETRIES = 8;
const POPOVER_WIDTH = 320;
const POPOVER_MARGIN = 12;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** placement=auto — 아래 공간 우선, 부족하면 위/좌/우. 뷰포트 클램프. */
function resolvePopoverStyle(rect: DOMRect | null, placement: TourStep['placement']): CSSProperties {
  if (!rect) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: POPOVER_WIDTH,
    };
  }

  const viewportWidth = window.innerWidth || 1024;
  const viewportHeight = window.innerHeight || 768;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  let resolved = placement && placement !== 'auto' ? placement : undefined;
  if (!resolved) {
    resolved = spaceBelow >= 180 || spaceBelow >= spaceAbove ? 'bottom' : 'top';
  }

  const left = clamp(
    rect.left + rect.width / 2 - POPOVER_WIDTH / 2,
    POPOVER_MARGIN,
    Math.max(POPOVER_MARGIN, viewportWidth - POPOVER_WIDTH - POPOVER_MARGIN)
  );

  if (resolved === 'top') {
    return {
      position: 'fixed',
      left,
      top: Math.max(POPOVER_MARGIN, rect.top - POPOVER_MARGIN),
      transform: 'translateY(-100%)',
      width: POPOVER_WIDTH,
    };
  }
  if (resolved === 'left') {
    return {
      position: 'fixed',
      top: clamp(rect.top + rect.height / 2, POPOVER_MARGIN, viewportHeight - POPOVER_MARGIN),
      left: Math.max(POPOVER_MARGIN, rect.left - POPOVER_MARGIN),
      transform: 'translate(-100%, -50%)',
      width: POPOVER_WIDTH,
    };
  }
  if (resolved === 'right') {
    return {
      position: 'fixed',
      top: clamp(rect.top + rect.height / 2, POPOVER_MARGIN, viewportHeight - POPOVER_MARGIN),
      left: Math.min(viewportWidth - POPOVER_MARGIN, rect.right + POPOVER_MARGIN),
      transform: 'translateY(-50%)',
      width: POPOVER_WIDTH,
    };
  }
  // bottom (기본)
  return {
    position: 'fixed',
    left,
    top: Math.min(viewportHeight - POPOVER_MARGIN, rect.bottom + POPOVER_MARGIN),
    width: POPOVER_WIDTH,
  };
}

/**
 * queue #669 §UAT-2026-07-09-R669 §R669-2 — 온보딩 투어 코치마크(자체 구현 · 외부 라이브러리 0).
 *
 * `useOnboardingTour`(게이팅 훅)가 자동시작/localStorage/`forceStart` 를 관리하고, 이 컴포넌트는
 * 오버레이 · spotlight · popover · step 진행 · 키보드 · 포커스만 담당(ui-kit 승격 시 이 프레젠테이션
 * 프리미티브만 이전 가능하게 분리 — §R669-0).
 *
 * queue #695 §UAT-2026-07-14-R695 §R695-1 — 비모달 스포트라이트로 전환(강제 모달 → 비모달).
 * 루트 오버레이는 `pointerEvents:'none'`(히트 영역 아님) + `aria-modal={false}` 라 배경
 * 클릭·스크롤·Tab 이 그대로 통과한다. 팝오버 카드만 `pointerEvents:'auto'`.
 *
 * 접근성: `role="dialog" aria-modal="false"` + `Esc`(전역 — 건너뛰기) · 외부클릭(`pointerdown`
 * capture · `preventDefault` 미호출로 클릭이 대상에 그대로 전달 — 한 번의 클릭으로 "닫힘 + 원래
 * 동작 실행") · `→`/`Enter`(다음)·`←`(이전)는 **포커스가 팝오버 내부일 때만** 처리(배경 입력·제출
 * 무간섭) · Tab 은 자유 이동(포커스 트랩 없음) · 스텝 전환마다 주 버튼 포커스 · 종료 시 직전 포커스
 * 복귀(단, 외부클릭으로 닫힌 경우 사용자가 새로 포커스한 요소를 되돌리지 않는 가드 포함) · 스텝
 * 본문 `aria-live="polite"`(비모달이라 전환이 자동 낭독되지 않음).
 *
 * 5상태: default(비활성 — null 렌더) / dialog(활성 — 비모달 스포트라이트) / error(대상
 * `targetSelector` 미발견 시 해당 스텝 자동 스킵 · 콘솔 경고만 · 크래시 없음) — loading/empty 는
 * 정적 스텝 정의 + 동기 `querySelector` 라 발생하지 않는다(§R669-2 · §R695-4).
 */
export function OnboardingTour({ isOpen, steps, onDismiss }: OnboardingTourProps) {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const step = steps[stepIndex];
  const total = steps.length;
  const isLastStep = stepIndex >= steps.length - 1;

  // 오픈 시 1스텝부터 시작 + 직전 포커스 요소 저장
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    }
  }, [isOpen]);

  const advance = useCallback(() => {
    setStepIndex((idx) => (idx + 1 >= steps.length ? idx : idx + 1));
  }, [steps.length]);

  const retreat = useCallback(() => {
    setStepIndex((idx) => Math.max(0, idx - 1));
  }, []);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onDismiss();
      return;
    }
    advance();
  }, [isLastStep, advance, onDismiss]);

  // 대상 측정 + scrollIntoView + rAF 재시도 + 미발견 시 자동 스킵(§R669-2 error)
  useEffect(() => {
    if (!isOpen || !step) return undefined;
    if (!step.targetSelector) {
      setRect(null);
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    const selector = step.targetSelector;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        // jsdom(vitest) 은 scrollIntoView 미구현 — 존재 시에만 호출(실 브라우저/e2e 는 정상 동작)
        el.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        setRect(el.getBoundingClientRect());
        return;
      }
      attempts += 1;
      if (attempts > MAX_TARGET_RETRIES) {
        // eslint-disable-next-line no-console
        console.warn(`[OnboardingTour] 대상 미발견 — 스텝 자동 스킵: ${step.key} (${selector})`);
        if (isLastStep) {
          onDismiss();
        } else {
          advance();
        }
        return;
      }
      rafId = requestAnimationFrame(measure);
    };
    rafId = requestAnimationFrame(measure);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
    // step/stepIndex 변경마다 재측정 — isLastStep/advance/onDismiss 는 stepIndex 파생값이라
    // 중복 재실행 방지를 위해 의도적으로 제외(무한루프 방지, 동작은 stepIndex 에 종속됨).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stepIndex]);

  // resize/scroll 재계산(rAF 게이트로 throttle)
  useEffect(() => {
    if (!isOpen || !step?.targetSelector) return undefined;
    const selector = step.targetSelector;
    let ticking = false;
    const recalc = () => {
      ticking = false;
      const el = document.querySelector<HTMLElement>(selector);
      if (el) setRect(el.getBoundingClientRect());
    };
    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(recalc);
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [isOpen, step]);

  // 스텝 전환마다 주 버튼(다음/마침)으로 포커스 이동(rAF — FormDialog/CommandPalette 관례)
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => primaryButtonRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen, stepIndex]);

  // 종료 시 직전 포커스 요소로 복귀 — §R695-3(d): 외부클릭으로 닫힌 경우 브라우저가 이미 클릭한
  // 요소로 포커스를 옮겨놓았을 수 있다(비모달 언마운트 시 activeElement 는 body 로 리셋되는 경우와
  // 구분) — activeElement 가 body/직전 요소가 아니면(=사용자가 새로 포커스한 요소가 있으면) 복귀를
  // 생략해 방금 누른 요소에서 포커스를 빼앗지 않는다.
  useEffect(() => {
    if (isOpen) return undefined;
    const el = previousFocusRef.current;
    if (!el || !document.contains(el)) return undefined;
    const active = document.activeElement;
    if (active && active !== document.body && active !== el) {
      return undefined;
    }
    el.focus();
    return undefined;
  }, [isOpen]);

  // 외부클릭(팝오버 밖 pointerdown) = 즉시 dismiss — §R695-3(b): preventDefault 금지로 동일
  // pointerdown→click 시퀀스가 대상 요소에 그대로 전달돼 "닫힘 + 원래 동작 실행"이 한 클릭으로 된다.
  // 루트 오버레이는 pointerEvents:'none' 이라 이벤트 target 은 처음부터 배경 요소다.
  useEffect(() => {
    if (!isOpen) return undefined;

    function handlePointerDown(e: PointerEvent) {
      const container = dialogRef.current;
      if (!container || container.contains(e.target as Node)) return;
      onDismiss();
    }

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, onDismiss]);

  // 키보드 — Esc(전역 · 건너뛰기) · →/Enter(다음·마지막은 마침)/←(이전)는 포커스가 팝오버
  // 내부일 때만 처리(§R695-3(c) — 배경 입력·제출 무간섭). Tab 포커스 트랩은 제거(비모달이라
  // 배경으로 자유 이동 가능해야 함).
  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
        return;
      }
      const container = dialogRef.current;
      const focusInsidePopover = !!container && !!document.activeElement && container.contains(document.activeElement);
      if (!focusInsidePopover) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        retreat();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onDismiss, handleNext, retreat]);

  const titleId = 'onboarding-tour-title';
  const bodyId = 'onboarding-tour-body';

  const popoverStyle = useMemo(() => resolvePopoverStyle(rect, step?.placement), [rect, step?.placement]);

  if (!isOpen || !step) return null;

  return (
    <div
      role="dialog"
      aria-modal={false}
      aria-label={t('gettingStartedGuide.tour.aria')}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      ref={dialogRef}
      data-testid="onboarding-tour"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        pointerEvents: 'none',
        background: rect ? 'transparent' : 'var(--color-overlay-backdrop)',
      }}
    >
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 0 0 9999px var(--color-overlay-backdrop), 0 0 0 3px var(--color-brand-primary)',
            pointerEvents: 'none',
          }}
        />
      )}

      <div
        style={{
          ...popoverStyle,
          pointerEvents: 'auto',
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--spacing-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-3)',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
          {t('gettingStartedGuide.tour.step', { current: stepIndex + 1, total })}
        </span>
        <h2 id={titleId} style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}>
          {t(step.titleKey)}
        </h2>
        <p id={bodyId} aria-live="polite" style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {t(step.bodyKey)}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-2)' }}>
          <button
            type="button"
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              padding: 'var(--spacing-1) var(--spacing-2)',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('gettingStartedGuide.tour.skip')}
          </button>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={retreat}
                style={{
                  background: 'none',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('gettingStartedGuide.tour.prev')}
              </button>
            )}
            <button
              ref={primaryButtonRef}
              type="button"
              onClick={handleNext}
              style={{
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-1) var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {isLastStep ? t('gettingStartedGuide.tour.done') : t('gettingStartedGuide.tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
